import React, { useEffect, useState } from "react";
import { Send, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { parApi, parErr } from "@/lib/partnerApi";
import { inputCls, labelCls, GoldButton, ErrorLine } from "@/components/nds/windows/resident/ui";
import { AttachButton, FileChips } from "./MemoFiles";
import { useAuth } from "@/lib/auth";
import RecipientPicker from "./RecipientPicker";
import { MemoAssist } from "@/components/memo/MemoAssist";

const rname = (r) => `${r.preferred_name || r.first_name || ""} ${r.last_name || ""}`.trim();

export default function MemoCompose({ side, onDone, onCancel }) {
  const staff = side === "staff";
  const me = useAuth().user?.id;
  const [picked, setPicked] = useState(new Set());
  const [f, setF] = useState({ resident_id: "", subject: "", body: "", priority: "normal", send_text: true });
  const [staffList, setStaffList] = useState([]);
  const [files, setFiles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [residents, setResidents] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  useEffect(() => {
    if (staff) parApi.get("/memos/partners").then((d) => setPartners(d.partners)).catch(() => {});
    parApi.get("/memos/residents").then((d) => setResidents(d.residents)).catch(() => {});
    parApi.get("/memos/staff").then((d) => setStaffList(d.staff)).catch(() => {});
  }, [staff]);
  const keys = [...picked];
  const pids = keys.filter((k) => k.startsWith("p:")).map((k) => k.slice(2));
  const sids = keys.filter((k) => k.startsWith("s:")).map((k) => k.slice(2));
  const only = keys.length === 1 ? keys[0] : null;
  const partner = only?.startsWith("p:") ? partners.find((p) => `p:${p.id}` === only) : null;
  const person = only?.startsWith("s:") ? staffList.find((u) => `s:${u.id}` === only) : null;
  const ok = f.subject.trim() && f.body.trim() && (!staff || keys.length);
  const pOpts = partners.map((p) => ({ key: `p:${p.id}`, label: `${p.first_name} ${p.last_name}${p.organization ? ` · ${p.organization}` : ""}` }));
  const sOpts = staffList.filter((u) => u.id !== me).map((u) => ({ key: `s:${u.id}`, label: `${u.name}${u.title ? ` · ${u.title}` : ""}` }));
  const send = async () => {
    setErr(null); setBusy(true);
    const base = { subject: f.subject, body: f.body, priority: f.priority, send_text: f.send_text && partner?.has_phone !== false, resident_id: f.resident_id || null, attachment_ids: files.map((x) => x.id) };
    try {
      if (!keys.length) {
        const m = await parApi.post("/memos", base);
        toast.success(`Memo sent to TEA staff${m.sms?.status === "sent" ? " · texted" : ""}`);
        return onDone(m);
      }
      const r = await parApi.post("/memos/bulk", { ...base, partner_ids: pids, staff_ids: sids });
      toast.success(`Memo sent to ${r.sent} ${r.sent === 1 ? "person" : "people"}${r.texted ? ` · ${r.texted} texted` : ""}`);
      onDone(r.sent === 1 ? r.memos[0] : null);
    } catch (e) { setErr(parErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="pmemo-compose" className="space-y-3">
      <button type="button" onClick={onCancel} data-testid="pmemo-compose-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold"><ArrowLeft className="h-3.5 w-3.5" /> Memos</button>
      <RecipientPicker partners={staff ? pOpts : []} staff={sOpts} picked={picked} setPicked={setPicked} placeholder={staff ? "Tick one or more partners or staff — each gets their own copy." : "Leave empty to send to any TEA staff."} />
      <div><label className={labelCls}>About a resident (optional)</label>
        <select value={f.resident_id} onChange={set("resident_id")} data-testid="pmemo-resident" className={inputCls}>
          <option value="">No resident</option>
          {residents.map((r) => <option key={r.id} value={r.id}>{rname(r)}</option>)}
        </select>
      </div>
      <input value={f.subject} onChange={set("subject")} placeholder="Subject" maxLength={200} data-testid="pmemo-subject" className={inputCls} />
      <textarea rows={6} value={f.body} onChange={set("body")} placeholder={`Message to ${partner ? "the partner" : person ? person.name : keys.length > 1 ? `${keys.length} recipients` : "TEA staff"}…`} data-testid="pmemo-body" className={inputCls} />
      <MemoAssist value={f.body} onChange={(v) => setF((x) => ({ ...x, body: v }))} id="pmemo-body" />
      <FileChips files={files} onRemove={(id) => setFiles(files.filter((x) => x.id !== id))} testid="pmemo-compose-files" />
      <div className="flex flex-wrap items-center gap-4">
        <AttachButton onAdd={(x) => setFiles((fs) => [...fs, x])} testid="pmemo-attach" />
        <label className={`text-[11px] inline-flex items-center gap-1.5 ${f.priority === "urgent" ? "text-red-300" : "text-nex-muted"}`}>
          <input type="checkbox" checked={f.priority === "urgent"} onChange={(e) => setF({ ...f, priority: e.target.checked ? "urgent" : "normal" })} data-testid="pmemo-urgent" />
          <AlertTriangle className="h-3 w-3" /> Urgent
        </label>
        {staff && (
          <label className="text-[11px] text-nex-muted inline-flex items-center gap-1.5">
            <input type="checkbox" checked={f.send_text && partner?.has_phone !== false} disabled={partner?.has_phone === false} onChange={set("send_text")} data-testid="pmemo-send-text" /> Text them{partner && !partner.has_phone ? " (no phone on file)" : ""}
          </label>
        )}
        <GoldButton onClick={send} disabled={!ok || busy} data-testid="pmemo-send" className="ml-auto"><Send className="h-3 w-3" /> Send memo</GoldButton>
      </div>
      {!staff && <p className="text-[11px] text-nex-muted">{!keys.length ? "TEA staff get a text when your memo arrives." : keys.length > 1 || person?.has_phone ? "They get a text when your memo arrives." : "They'll see it next time they sign in (no phone on file)."}</p>}
      <ErrorLine testId="pmemo-compose-error">{err}</ErrorLine>
    </div>
  );
}
